import React, { useCallback, useEffect, useMemo, useState } from "react";
import { scrubSurveyImages } from "./formTheme";
import { gcpDownloadUrl } from "./gcpDownloadUrl";
import Axios from "axios";
import JSZip from "jszip";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
import { applyStackgoTheme } from "./stackgo-theme";

StylesManager.applyTheme("defaultV2");

const cloneJson = (value) => JSON.parse(JSON.stringify(value || {}));

const isDataUrl = (value) =>
  typeof value === "string" && value.startsWith("data:");

const isFileLikeObject = (value) => {
  if (!value || typeof value !== "object") return false;
  return (
    ("name" in value || "filename" in value) &&
    ("content" in value || "data" in value)
  );
};

const isStoredFileObject = (value) =>
  value &&
  typeof value === "object" &&
  typeof value.key === "string" &&
  (typeof value.name === "string" || typeof value.filename === "string");

function collectFileObjects(node, out = []) {
  if (!node) return out;

  if (Array.isArray(node)) {
    if (node.length && node.every(isFileLikeObject)) {
      node.forEach((file) => out.push(file));
      return out;
    }
    node.forEach((child) => collectFileObjects(child, out));
    return out;
  }

  if (isFileLikeObject(node)) {
    out.push(node);
    return out;
  }

  if (typeof node === "object") {
    Object.values(node).forEach((child) => collectFileObjects(child, out));
  }

  return out;
}

function collectStoredFiles(node, out = []) {
  if (!node) return out;

  if (Array.isArray(node)) {
    node.forEach((child) => collectStoredFiles(child, out));
    return out;
  }

  if (isStoredFileObject(node)) {
    out.push(node);
    return out;
  }

  if (typeof node === "object") {
    Object.values(node).forEach((child) => collectStoredFiles(child, out));
  }

  return out;
}

const ensureUniqueName = (name, usedNames) => {
  if (!usedNames.has(name)) {
    usedNames.set(name, 1);
    return name;
  }

  const parts = name.split(".");
  const ext = parts.length > 1 ? parts.pop() : "";
  const base = parts.join(".");
  let index = usedNames.get(name);
  let candidate;

  do {
    candidate = ext ? `${base}(${index}).${ext}` : `${base}(${index})`;
    index += 1;
  } while (usedNames.has(candidate));

  usedNames.set(name, index);
  usedNames.set(candidate, 1);
  return candidate;
};

const dataUrlToBase64 = (value) => {
  if (typeof value !== "string") return null;
  if (!value) return null;
  if (value.startsWith("data:")) {
    const commaIndex = value.indexOf(",");
    return commaIndex === -1 ? null : value.substring(commaIndex + 1);
  }
  return /^[A-Za-z0-9+/=\n\r]+$/.test(value) ? value.replace(/\s/g, "") : null;
};

export function ReadOnlySurveyResponse({ response, showActions = true }) {
  const [responseData, setResponseData] = useState({});

  useEffect(() => {
    let cancelled = false;

    const hydrateFiles = async () => {
      if (!response) {
        setResponseData({});
        return;
      }

      const nextData = cloneJson(response.data);
      setResponseData(nextData);

      const storedFiles = collectStoredFiles(nextData);
      if (storedFiles.length === 0) {
        return;
      }

      await Promise.allSettled(
        storedFiles.map(async (file) => {
          if (isDataUrl(file.content)) return;
          const name = file.name || file.filename || "";
          const fileResponse = await Axios.get(
            gcpDownloadUrl({ key: file.key, name, responseId: response.id })
          );
          file.content = fileResponse.data.blob;
        })
      );

      if (!cancelled) {
        setResponseData(cloneJson(nextData));
      }
    };

    hydrateFiles();

    return () => {
      cancelled = true;
    };
  }, [response]);

  const savePdf = useCallback(async () => {
    if (!response?.id) return;

    try {
      const pdf = await Axios.get(`/api/response/${response.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(pdf.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${response.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF", err);
      alert("Failed to download PDF. See console for details.");
    }
  }, [response]);

  const downloadFiles = useCallback(() => {
    const files = collectFileObjects(responseData);

    if (files.length === 0) {
      alert("No file attachments detected in this response.");
      return;
    }

    const zip = new JSZip();
    const usedNames = new Map();
    let addedCount = 0;

    files.forEach((file) => {
      const dataField = file.content || file.data;
      const base64 = dataUrlToBase64(dataField);
      if (!base64) return;

      const rawName = file.name || file.filename || file.key || "file";
      zip.file(ensureUniqueName(rawName, usedNames), base64, { base64: true });
      addedCount += 1;
    });

    if (addedCount === 0) {
      alert("File attachments are still loading or could not be downloaded.");
      return;
    }

    zip.file(
      "manifest.json",
      JSON.stringify(
        files.map((file) => ({
          name: file.name || file.filename,
          key: file.key,
          type: file.type,
          hasContent: !!(file.content || file.data),
        })),
        null,
        2
      )
    );

    zip
      .generateAsync({ type: "blob" })
      .then((content) => {
        const url = URL.createObjectURL(content);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${response?.id || "response"}-files.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Failed to create zip", err);
        alert("Failed to generate ZIP file. See console for details.");
      });
  }, [response, responseData]);

  const survey = useMemo(() => {
    if (!response?.form_data) return null;

    const model = scrubSurveyImages(new Model(response.form_data));
    applyStackgoTheme(model);
    model.data = responseData;
    model.mode = "display";

    model.onDownloadFile.add((sender, options) => {
      if (isDataUrl(options.content)) {
        options.callback("success", options.content);
        return;
      }
      options.callback("success", "");
    });

    if (showActions) {
      model.addNavigationItem({
        id: "pdf-export",
        title: "Save as PDF",
        action: () => savePdf(),
      });
      model.addNavigationItem({
        id: "download-files",
        title: "Download Files",
        action: () => downloadFiles(),
      });
    }

    return model;
  }, [downloadFiles, response, responseData, savePdf, showActions]);

  if (!response?.form_data) return null;
  if (!survey) return null;

  return <Survey model={survey} />;
}

export default ReadOnlySurveyResponse;
